/* eslint-disable max-len */
import React from 'react';
import PropTypes from 'prop-types';
import SweetAlert from 'react-bootstrap-sweetalert';
import Gravatar from 'react-gravatar';
import Moment from 'react-moment';
// @material-ui/icons
import ClipboardIcon from '@material-ui/icons/FileCopy';
// @material-ui/core components
import TextField from '@material-ui/core/TextField';
import userProfileStyles from 'assets/jss/material-dashboard-pro-react/views/userProfileStyles';
import withStyles from '@material-ui/core/styles/withStyles';
// global variables
import { exchangeRateCurrencies, userKinds, productionUrl } from 'config';
// core components
import Agency from '@material-ui/icons/SupervisorAccountRounded';
import Card from 'components/UI/Card/Card';
import GridItem from 'components/UI/Grid/GridItem';
import CardBody from 'components/UI/Card/CardBody';
import CardAvatar from 'components/UI/Card/CardAvatar';
import GridContainer from 'components/UI/Grid/GridContainer';
import Button from 'components/UI/CustomButtons/Button';
import WithPermission from 'components/User/WithPermission';
import { withTranslation } from 'react-i18next';
import agIcon from 'assets/img/AgencyIco.svg';
import userAgencyIcon from 'assets/img/UserAgencyIco.svg';
import { formatRoute } from 'react-router-named-routes';
import RouterContext from 'helpers/routerContext';
// our components
import AuthContext from 'helpers/authContext';
import agencyPaymentMethodTextHelper from 'helpers/agencyPaymentMethodTextHelper';
import ArrowForward from '@material-ui/icons/ArrowForward';
import { CREDIT, AD, REVIEWS } from 'routes/paths';
import { getUserPhotoUrl } from 'helpers/functions';

const reviewListPermissions = ['list_review', 'reviews.list_review'];

const reviewSummaryStyle = {
  display: 'flex',
  alignItems: 'baseline',
  flexWrap: 'wrap',
};

const reviewCountStyle = {
  color: '#777',
  fontSize: 13,
  marginLeft: 8,
};

const reviewLinksStyle = {
  display: 'flex',
  flexDirection: 'column',
  marginTop: 8,
};

const reviewLinkStyle = {
  color: '#217DB2',
  marginTop: 4,
  textDecoration: 'underline',
};

const allowViewCredit = (allPermissions, user, me) => {
  if (allPermissions.some(({ codename }) => codename === 'view_user')) {
    return true;
  }
  if (user.id === me.id) {
    return true;
  }
  return false;
};

const hasReviews = reviewCount => parseInt(reviewCount, 10) > 0;

const formatRating = rate => {
  const rating = parseFloat(rate);
  if (Number.isNaN(rating)) {
    return '---';
  }
  return rating.toFixed(2);
};

const getReviewCountText = (t, reviewCount) =>
  t(
    parseInt(reviewCount, 10) === 1
      ? 'user.info.reviews.count_singular'
      : 'user.info.reviews.count',
    { count: reviewCount },
  );

/**
 * @description
 * Stateless component to show user info
 *
 * @param {Object} props - Component props.
 * @returns {undefined} - No returns.
 */
class UserInfo extends React.Component {
  static contextType = RouterContext;

  constructor(props) {
    super(props);
    this.state = {
      modalIsOpen: false,
      amount: 0,
      action: 'add',
    };
  }

  /**
   * @description
   * Redirect to user/read/ route.
   *
   * @param {number} id - Id of the user to read.
   */
  handleDetails = id => {
    const history = this.context;
    history.push(formatRoute(`/user/details/:id/#logs`, { id }));
  };

  /**
   * @description
   * Open change credit modal.
   *
   * @param {string} action - Action to apply to credit.
   */
  handleOpenModal = action => {
    this.setState({ modalIsOpen: true, action });
  };

  /**
   * @description
   * Callback to close change credit modal if user close it manually.
   */
  handleCloseModal = () => {
    this.setState({ modalIsOpen: false, amount: 0 });
  };

  /**
   * @description
   * Handle submit to confirm change credit.
   */
  handleSubmit = () => {
    const { changeCredit } = this.props;
    const { amount, action } = this.state;
    changeCredit({
      amount,
      type: action === 'add' ? 'INCREMENT' : 'DECREMENT',
    });
    this.setState({
      modalIsOpen: false,
      amount: 0,
    });
  };

  /**
   * @description
   * Map userType to specific strings.
   *
   * @param {string} userType - User type.
   * @param {string} action - Action.
   * @returns {string} Mapped user types.
   */
  handleMapPermissions = (userType, action) => {
    if (userType === userKinds.USER_AGENCY)
      return [`${action}_useragency`, `${action}_ownership_useragency`];
    if (userType === userKinds.AGENCY) return [`${action}_agency`];
    if (userType === userKinds.USER) return [`${action}_user`];
    return [];
  };

  render() {
    const { classes, data, t } = this.props;
    const { modalIsOpen, action, amount } = this.state;

    /**
     * @description
     * Format the DATE received from the server.
     *
     * @param {string} date - DATE received from the server.
     * @returns {Object} Converted and translated date.
     */
    const dateFormat = date => {
      const calendarStrings = {
        lastDay: `[${t('common.calendar.yesterday')} ] LT`,
        sameDay: `[${t('common.calendar.today')} ] LT`,
        lastWeek: '[] dddd[, ] LT',
        sameElse: 'L',
      };
      // 2019-02-08T15:59-0500 date for testing
      return <Moment date={date} calendar={calendarStrings} />;
    };
    // eslint-disable-next-line no-underscore-dangle
    const isAgency = data.__typename === 'AgencyType';
    // eslint-disable-next-line no-underscore-dangle
    const isUserAgency = data.__typename === 'UserAgencyType';
    return (
      <AuthContext.Consumer>
        {me => (
          <React.Fragment>
            <SweetAlert
              title={t('user.info.change_credit_modal.title')}
              showConfirm={false}
              showCancel={false}
              onCancel={() => this.handleCloseModal()}
              onConfirm={() => {}}
              closeOnClickOutside
              customClass={classes.adModalConfirmation}
              allowEscape
              show={modalIsOpen}
            >
              <div className={classes.userDetails}>
                <h6>
                  {`${t('user.info.change_credit_modal.value')} 
            ${
              action === 'add'
                ? t('user.info.increase')
                : t('user.info.decrease')
            }`}
                </h6>
                <TextField
                  id="creditToChange"
                  InputProps={{
                    value: amount,
                    onChange: e => this.setState({ amount: e.target.value }),
                    type: 'number',
                    inputProps: { min: 0 },
                    style: { width: '100px' },
                  }}
                />
              </div>
              <div className={classes.userDetails}>
                <h6>{t('user.info.change_credit_modal.final')}</h6>
                <h5>
                  {data &&
                    `${data.credit} -> 
                  ${
                    action === 'add'
                      ? // eslint-disable-next-line radix
                        data.credit + parseInt(amount)
                      : // eslint-disable-next-line radix
                        data.credit - parseInt(amount)
                  }`}
                </h5>
              </div>

              <div className={classes.changeCreditModalBtnsSection}>
                <Button
                  color="primary"
                  simple
                  onClick={() => this.handleCloseModal()}
                  id="cancelChangeCredit"
                >
                  {t('user.info.change_credit_modal.cancel')}
                </Button>
                <Button
                  color="success"
                  onClick={() => this.handleSubmit()}
                  id="confirmChangeCredit"
                >
                  {`${
                    action === 'add'
                      ? t('user.info.increase')
                      : t('user.info.decrease')
                  } ${t('user.info.change_credit_modal.credit')}`}
                </Button>
              </div>
            </SweetAlert>
            <GridContainer>
              <GridItem xs={12}>
                <Card profile>
                  <CardBody profile>
                    {data.id !== '0' ? (
                      <>
                        <div className={classes.userInfo}>
                          <div className={classes.avatarContainer}>
                            <CardAvatar data-cy="userAvatar" profile>
                              {data && data.photo ? (
                                <img
                                  src={getUserPhotoUrl(data.photo)}
                                  alt="Avatar"
                                />
                              ) : (
                                <Gravatar email={data.email || data.fullname} />
                              )}
                            </CardAvatar>
                            {isAgency && (
                              <img
                                src={agIcon}
                                alt="AgencyLogo"
                                className={classes.agencyLogo}
                              />
                            )}
                            {isUserAgency && (
                              <img
                                src={userAgencyIcon}
                                alt="UserAgencyLogo"
                                className={classes.agencyLogo}
                              />
                            )}
                          </div>
                          <div>
                            <h4
                              data-cy="userFullname"
                              id={`user-info-${data ? data.id : '0'}`}
                              className={classes.cardTitle}
                            >
                              {data && data.fullname}
                            </h4>
                            <h5
                              data-cy="userRank"
                              className={classes.cardCategory}
                            >
                              {data && data.rank}
                            </h5>
                            {isUserAgency && (
                              <h6>{t('user.details.user_agency')}</h6>
                            )}
                            {isAgency && <h6>{t('user.details.agency')}</h6>}
                          </div>
                        </div>
                        <div className={classes.userDetails}>
                          <h5 data-cy="userEmail">{data && data.email}</h5>
                        </div>
                        <div className={classes.userDetails}>
                          <h5 data-cy="userPhone">{data && data.phone}</h5>
                        </div>
                        <div className={classes.userDetails}>
                          <h5 data-cy="userLocation">
                            {data &&
                              data.location &&
                              data.location.province &&
                              (data.location.municipality
                                ? `${data.location.municipality.name}, ${data.location.province.name}`
                                : data.location.province.name)}
                          </h5>
                        </div>
                        {!data.source && (
                          <div className={classes.userDetails}>
                            <h5 data-cy="userLink">
                              <a
                                rel="noopener noreferrer"
                                target="_blank"
                                href={`${productionUrl}/profile/${data.publicId}`}
                                className={classes.adLinks}
                              >
                                {`${productionUrl}/profile/${data.publicId}`}
                              </a>
                            </h5>
                          </div>
                        )}
                        <div className={classes.userAds}>
                          <WithPermission allowPermissions={['list_ad']}>
                            <a
                              data-test-id="seeAdListUser"
                              href={`${formatRoute(AD.LIST)}?userId=${data.id}`}
                            >
                              {t('user.info.ads')}
                            </a>
                          </WithPermission>
                        </div>
                        <div
                          className={classes.userDetails}
                          data-cy="userReviews"
                        >
                          <h6>{t('user.info.reviews.title')}</h6>
                          {hasReviews(data.reviewCount) ? (
                            <div style={reviewSummaryStyle}>
                              <h5 data-cy="userReviewRate">
                                {t('user.info.reviews.rating', {
                                  rate: formatRating(data.rate),
                                })}
                              </h5>
                              <span
                                style={reviewCountStyle}
                                data-cy="userReviewCount"
                              >
                                {getReviewCountText(t, data.reviewCount)}
                              </span>
                            </div>
                          ) : (
                            <h5 data-cy="userReviewEmpty">
                              {t('user.info.reviews.empty')}
                            </h5>
                          )}
                          <WithPermission
                            allowPermissions={reviewListPermissions}
                          >
                            <div style={reviewLinksStyle}>
                              <a
                                data-test-id="seeReviewsWrittenByUser"
                                href={`${formatRoute(REVIEWS.LIST)}?reviewer=${
                                  data.id
                                }`}
                                style={reviewLinkStyle}
                              >
                                {t('user.info.reviews.written_by_user')}
                              </a>
                              <a
                                data-test-id="seeReviewsReceivedByUser"
                                href={`${formatRoute(REVIEWS.LIST)}?reviewee=${
                                  data.id
                                }`}
                                style={reviewLinkStyle}
                              >
                                {t('user.info.reviews.received_by_user')}
                              </a>
                            </div>
                          </WithPermission>
                        </div>
                        <WithPermission allowPermissions={['view_user']}>
                          <div className={classes.userDetails}>
                            <h6>{t('user.info.last')}</h6>
                            <h5>{data && dateFormat(data.lastLogin)}</h5>
                          </div>
                          <div className={classes.userDetails}>
                            <h6>{t('user.info.creation')}</h6>
                            <h5>{data && dateFormat(data.joined)}</h5>
                          </div>
                          <div className={classes.userDetails}>
                            <h6>{t('user.info.revolico_joined_on')}</h6>
                            <h5>
                              {data &&
                                data.revolicoJoinedOn &&
                                dateFormat(data.revolicoJoinedOn)}
                            </h5>
                          </div>
                          {data && data.cardProvider && (
                            <WithPermission
                              allowPermissions={['view_card_provider']}
                            >
                              <div className={classes.userDetails}>
                                <h6>{t('user.info.card_provider')}</h6>
                                <h5>{data.cardProvider}</h5>
                              </div>
                            </WithPermission>
                          )}
                          {data && data.source && (
                            <div className={classes.userDetails}>
                              <h6>{t('user.info.source')}</h6>
                              <h5>{data.source}</h5>
                            </div>
                          )}
                          {data && data.allowedIps && (
                            <WithPermission allowPermissions={[]}>
                              <div className={classes.userDetails}>
                                <h6>{t('user.info.allowedIp')}</h6>
                                {data.allowedIps.map(ips => (
                                  <h5>{ips}</h5>
                                ))}
                              </div>
                            </WithPermission>
                          )}
                        </WithPermission>
                        <WithPermission
                          allowPermissionFunc={userPermissions =>
                            allowViewCredit(userPermissions, data, me)
                          }
                        >
                          <div className={classes.userDetails}>
                            <h6>{t('user.info.credit')}</h6>
                            <div className={classes.actions}>
                              {data && (
                                <WithPermission
                                  allowPermissions={[
                                    'list_credittransaction',
                                    'list_ownership_credittransaction',
                                  ]}
                                  alt={data.credit}
                                >
                                  <a
                                    data-test-id="seeTransactionHistory"
                                    href={`${formatRoute(CREDIT.LIST)}?user=${
                                      data.id
                                    }&userName=${data.fullname}`}
                                  >
                                    {data.credit}
                                  </a>
                                </WithPermission>
                              )}

                              <WithPermission
                                allowPermissions={this.handleMapPermissions(
                                  data.userType,
                                  'change',
                                )}
                              >
                                <WithPermission
                                  allowPermissions={[
                                    'add_admincredittransaction',
                                  ]}
                                >
                                  <Button
                                    simple
                                    color="transparent"
                                    id="user-credit-decrease"
                                    style={{
                                      padding: 0,
                                      margin: 0,
                                    }}
                                    onClick={() =>
                                      this.handleOpenModal('substract')
                                    }
                                  >
                                    {t('user.info.decrease')}
                                  </Button>
                                  <Button
                                    simple
                                    color="transparent"
                                    id="user-credit-increase"
                                    style={{
                                      padding: 0,
                                      margin: 0,
                                    }}
                                    onClick={() => this.handleOpenModal('add')}
                                  >
                                    {t('user.info.increase')}
                                  </Button>
                                </WithPermission>
                              </WithPermission>
                            </div>
                          </div>
                        </WithPermission>
                        {isAgency && (
                          <div id="agency-data">
                            <div className={classes.userDetails}>
                              <h6>
                                {t('user.info.agency.payment_method.title')}
                              </h6>
                              {data && (
                                <h5 data-cy="agencyPaymentMethod">
                                  {agencyPaymentMethodTextHelper(
                                    data.paymentMethod,
                                    t,
                                  )}
                                </h5>
                              )}
                            </div>
                            <GridContainer id="agency-commission-data">
                              <GridItem>
                                <div className={classes.userDetails}>
                                  <h6>
                                    {t('user.info.agency.commission.agency')}
                                  </h6>
                                  <h5 data-cy="userAgencyLead">
                                    {data && data.commissionLead}%
                                  </h5>
                                </div>
                              </GridItem>
                              <GridItem>
                                <div className={classes.userDetails}>
                                  <h6>
                                    {t('user.info.agency.commission.revolico')}
                                  </h6>
                                  <h5 data-cy="userAgencyLeadRevolico">
                                    {data && data.commissionLeadRevolico}%
                                  </h5>
                                </div>
                              </GridItem>
                            </GridContainer>
                            {data.exchangeRate && (
                              <WithPermission
                                allowPermissions={['view_current_exchangerate']}
                              >
                                <div className={classes.userDetails}>
                                  <h6>{t('user.info.agency.exchange_rate')}</h6>
                                  <h5 className={classes.verticalAlignment}>
                                    {exchangeRateCurrencies.USD} -{' '}
                                    {exchangeRateCurrencies.CUP}{' '}
                                    <ArrowForward
                                      style={{
                                        margin: '0px 8px',
                                        width: 18,
                                      }}
                                      color="disabled"
                                    />
                                    {data.exchangeRate[0]
                                      ? data.exchangeRate[0].rate
                                      : '---'}
                                  </h5>
                                </div>
                              </WithPermission>
                            )}
                          </div>
                        )}
                        {isUserAgency && (
                          <React.Fragment>
                            <GridContainer id="userAgency-data">
                              <GridItem xs={6}>
                                <div className={classes.userDetails}>
                                  <h6>{t('userAgency.lead')}</h6>
                                  <h5 data-cy="userAgencyLead">{data.lead}</h5>
                                </div>
                              </GridItem>
                            </GridContainer>
                            {data.leadAgency ? (
                              <GridContainer id="userAgency-leadAgencydata">
                                <GridItem xs={6}>
                                  <div className={classes.userDetails}>
                                    <h6>{t('userAgency.agency')}</h6>
                                    <h5>
                                      <a
                                        href={formatRoute(
                                          `/user/details/:id/#logs`,
                                          {
                                            id: data.leadAgency.id,
                                          },
                                        )}
                                        onClick={event => {
                                          this.handleDetails(
                                            data.leadAgency.id,
                                          );
                                          event.preventDefault();
                                        }}
                                      >
                                        {data.leadAgency.fullname}
                                      </a>
                                    </h5>
                                  </div>
                                </GridItem>
                              </GridContainer>
                            ) : null}
                          </React.Fragment>
                        )}
                        {isAgency && data.paymentMethod !== 'CASH' && (
                          <div className={classes.userDetails}>
                            <h6 style={{ margin: 0 }}>
                              {t('user.info.paymentLink')}
                              <span>
                                <Button
                                  simple
                                  justIcon
                                  color="primary"
                                  onClick={() => {
                                    navigator.clipboard
                                      .writeText(data.paymentLink)
                                      .then(() => {}, () => {});
                                  }}
                                >
                                  <ClipboardIcon />
                                </Button>
                              </span>
                            </h6>
                            {data && (
                              <a
                                href={data.paymentLink}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                {data.paymentLink}
                              </a>
                            )}
                          </div>
                        )}
                      </>
                    ) : (
                      <div
                        style={{
                          marginTop: 10,
                          fontSize: 15,
                          display: 'flex',
                          alignItems: 'center',
                        }}
                      >
                        <Agency />
                        <span>{`${t('promoted_ad.form.fields.private_user')} (${
                          data.fakeId
                        })`}</span>
                      </div>
                    )}
                  </CardBody>
                </Card>
              </GridItem>
            </GridContainer>
          </React.Fragment>
        )}
      </AuthContext.Consumer>
    );
  }
}

/**
 * UserInfo PropTypes
 * @param classes - prop styles received from withStyles
 * @param t - prop from HOC with string translations
 * @param data - prop user info received from container
 */

UserInfo.propTypes = {
  classes: PropTypes.instanceOf(Object).isRequired,
  data: PropTypes.shape({}).isRequired,
  changeCredit: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
};

export default withTranslation()(withStyles(userProfileStyles)(UserInfo));
